import type { AutocompletePlugin } from "@algolia/autocomplete-core";
import { GridItem } from "@ui/components/layout/grid/Grid";
import { Text } from "@ui/components/content/text/Text";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { CommonCMS } from "@lib/constants/contentful";
import { Flex } from "@ui/components/layout/flex/Flex";
import { NextLink } from "@components/ui/next-link/NextLink";
import { IconButton } from "@ui/components/forms/icon-button/IconButton";
import type { RecentSearchesPluginData } from "@algolia/autocomplete-plugin-recent-searches";
import { ECCOIcon } from "@ui/components/media-and-icons/ecco-icon/ECCOIcon";
import { defaultUnderlineStyle } from "@ui/css/common";
import { getPagePathByLocale, searchPage } from "@lib/constants/pagePaths";
import { useLocale } from "next-intl";
import { useMemo } from "react";
import type { BaseAutocompleteItemProps, RecentSearchesCollectionValue } from "./types";
import { GRID_AREAS } from "./AutoCompletePanelStyles";

export interface RecentSearchesItemProps
    extends BaseAutocompleteItemProps<RecentSearchesCollectionValue> {
    recentSearchesPlugin: AutocompletePlugin<
        RecentSearchesCollectionValue,
        RecentSearchesPluginData<RecentSearchesCollectionValue>
    >;
    onRemoveRecentSearch?: (id: string) => void;
}

export const RecentSearchesGrid = ({
    autocomplete,
    collection,
    onRemoveRecentSearch,
}: RecentSearchesItemProps) => {
    const { getMultiple } = useMicrocopy();
    const microcopies = getMultiple(CommonCMS.shared, [CommonCMS.searchBarRecentSearches]);

    const locale = useLocale();
    const searchPagePath = useMemo(() => {
        return getPagePathByLocale(searchPage, locale);
    }, [locale]);

    const { items } = collection || {};

    return (
        <GridItem
            display="flex"
            flexDir="column"
            gridColumnStart="1"
            gap="3"
            {...autocomplete?.getListProps()}
            gridArea={GRID_AREAS.suggestions}
        >
            <Text
                fontSize="desktopBodyTextSmall"
                fontWeight="bold"
                textTransform="uppercase"
                data-testid="autocompleteSearchesHeader"
            >
                {microcopies[CommonCMS.searchBarRecentSearches]}
            </Text>
            {items?.map(({ id, label }: { id: string; label: string }) => {
                return (
                    <Flex key={`${id}-${label}`} alignItems="center">
                        <NextLink
                            href={`${searchPagePath}?query=${encodeURIComponent(label)}`}
                            textTransform="capitalize"
                            textDecor="none"
                            fontSize="desktopHeading5"
                            color="black"
                            fontWeight="semibold"
                            sx={{
                                _hover: {
                                    "--b": "100%",
                                    color: "black",
                                    ...defaultUnderlineStyle,
                                },
                            }}
                        >
                            {label}
                        </NextLink>
                        <IconButton
                            height="100% !important"
                            variant="transparent"
                            data-testid="removesearchbutton"
                            aria-label="Remove Search"
                            onClick={() => onRemoveRecentSearch?.(id)}
                            icon={<ECCOIcon size="sm" name="close" />}
                        />
                    </Flex>
                );
            })}
        </GridItem>
    );
};
